import { compose, equals, lte, gte, includes, is, isNil, path, prop } from 'ramda';

// Largest dimension (px) allowed before downscaling.
// Document pages are 794×1123px at 96 DPI in portrait (1123×794 in landscape),
// so the largest page dimension is 1123px; 2246 = 2 × 1123 keeps margin for print sharpness.
const MAX_DIMENSION = 2246;
// Quality used when re-encoding JPEGs.
const JPEG_QUALITY = 0.8;
const MIMETYPE_JPEG = 'image/jpeg';
const MIMETYPE_PNG = 'image/png';
export const SUPPORTED_MIMETYPES = [MIMETYPE_JPEG, MIMETYPE_PNG];

const loadImageFromUrl = (objectUrl) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Lecture image échouée'));
  img.src = objectUrl;
});

// Decodes the image off the main thread when the browser supports it.
const decodeImage = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close()
      };
    } catch (_) {
      // Falls back to <img> decoding below.
    }
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageFromUrl(objectUrl);
    if (typeof img.decode === 'function') await img.decode();
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(objectUrl)
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
};

const computeTargetDimensions = (width, height) => {
  const largestDimension = Math.max(width, height);
  if (lte(largestDimension, MAX_DIMENSION)) return { width, height };
  const ratio = MAX_DIMENSION / largestDimension;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
};

const canvasToBlob = (canvas, mimetype, quality) => new Promise((resolve) => {
  canvas.toBlob(resolve, mimetype, quality);
});

// True when the file already went through `compressImage`.
export const isCompressedImage = compose(equals(true), path(['isCompressed']));

// Flags the file so callers (uploadFiles) do not compress it a second time.
const markCompressed = (file) => {
  if (is(Object, file)) file.isCompressed = true;
  return file;
};

// Compresses an image File/Blob via <canvas> (no third-party dependency).
// PDFs and unsupported mimetypes are returned untouched. The original file is
// returned whenever the compressed result is not actually lighter.
// Every returned file is flagged, so `uploadFiles` does not re-encode it.
const compressFile = async (file) => {
  const mimetype = prop('type', file);
  if (!includes(mimetype, SUPPORTED_MIMETYPES)) return file;

  let decoded;
  let canvas;
  try {
    decoded = await decodeImage(file);
    const { width, height } = computeTargetDimensions(decoded.width, decoded.height);

    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(decoded.source, 0, 0, width, height);
    // The decoded bitmap can weigh hundreds of MB: free it before the async encoding.
    decoded.release();
    decoded = undefined;

    const isJpeg = equals(MIMETYPE_JPEG, mimetype);
    const outputMimetype = isJpeg ? MIMETYPE_JPEG : MIMETYPE_PNG;
    const quality = isJpeg ? JPEG_QUALITY : undefined;
    const blob = await canvasToBlob(canvas, outputMimetype, quality);

    if (isNil(blob) || gte(prop('size', blob), prop('size', file))) return file;

    return new File([blob], prop('name', file), { type: outputMimetype });
  } catch (_) {
    return file;
  } finally {
    if (!isNil(decoded)) decoded.release();
    // Shrinking the canvas releases its backing store immediately instead of waiting for GC.
    if (!isNil(canvas)) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
};

export const compressImage = async (file) => markCompressed(await compressFile(file));
